import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

type MonacoLikeEditor = {
  onDidBlurEditorText: (listener: () => void) => { dispose: () => void };
  updateOptions: (options: Record<string, unknown>) => void;
};

type MonacoLanguage =
  | 'plaintext'
  | 'javascript'
  | 'typescript'
  | 'json'
  | 'html'
  | 'css'
  | 'sql'
  | 'xml'
  | 'markdown';

@Component({
  selector: 'app-code-input-control',
  template: `
    <div class="code-editor-wrapper" [class.disabled]="disabled">
      <ngx-monaco-editor
        class="code-editor"
        [attr.id]="id || null"
        [style.height.px]="height"
        [options]="editorOptionsResolved"
        [ngModel]="value"
        (ngModelChange)="onEditorValueChange($event)"
        (onInit)="onEditorInit($event)">
      </ngx-monaco-editor>
      @if (placeholder && !value) {
        <span class="code-editor-placeholder">{{ placeholder }}</span>
      }
    </div>
    `,
  styleUrls: ['./code-input-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeInputControlComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class CodeInputControlComponent implements ControlValueAccessor, AfterViewInit, OnChanges, OnDestroy {
  @Input() id?: string;
  @Input() language = 'HTML';
  @Input() editorOptions?: Record<string, unknown>;
  @Input() placeholder = '';
  @Input() height = 320;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};
  value = '';
  disabled = false;
  editorOptionsResolved: Record<string, unknown> = this.buildEditorOptions();
  private editorInstance?: MonacoLikeEditor;
  private blurSubscription?: { dispose: () => void };

  ngAfterViewInit(): void {
    // no-op
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['language'] || changes['editorOptions']) {
      this.editorOptionsResolved = this.buildEditorOptions();
      if (this.editorInstance) {
        this.editorInstance.updateOptions({
          ...this.editorOptionsResolved,
          readOnly: this.disabled,
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.blurSubscription?.dispose();
  }

  writeValue(value: unknown): void {
    this.value = value == null ? '' : String(value);
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.editorInstance?.updateOptions({ readOnly: this.disabled });
  }

  onEditorInit(editor: MonacoLikeEditor): void {
    this.editorInstance = editor;
    this.editorInstance.updateOptions(this.editorOptionsResolved);
    this.blurSubscription?.dispose();
    this.blurSubscription = editor.onDidBlurEditorText(() => this.onTouched());
  }

  onEditorValueChange(value: string): void {
    const nextValue = value ?? '';
    if (nextValue === this.value) {
      return;
    }
    this.value = nextValue;
    this.onChange(nextValue);
  }

  private normalizeLanguage(language?: string): MonacoLanguage {
    const normalized = (language || 'html').toLowerCase();
    const aliases: Record<string, MonacoLanguage> = {
      js: 'javascript',
      ts: 'typescript',
      yml: 'plaintext',
      yaml: 'plaintext',
      text: 'plaintext',
    };

    return (aliases[normalized] || normalized) as MonacoLanguage;
  }

  private buildEditorOptions(): Record<string, unknown> {
    return {
      language: this.normalizeLanguage(this.language),
      theme: 'vs-dark',
      automaticLayout: true,
      minimap: { enabled: false },
      scrollBeyondLastLine: false,
      wordWrap: 'on',
      readOnly: this.disabled,
      ...(this.editorOptions || {}),
    };
  }
}
